import { route, start, navigate, back } from './lib/router.js';
import { loadIndex, getIndex, StaleBuildError } from './lib/content.js';
import { subscribe, hasAnyProgress, local } from './lib/store.js';
import { initSync, onSync } from './lib/sync.js';
import { applySettings, watchSystemTheme, reducedMotion, setThemeColor } from './lib/theme.js';
import { html } from './lib/html.js';
import { icon } from './lib/icons.js';
import { renderNav, profileButton } from './components/chrome.js';
import { toast } from './components/toast.js';
import { bindGlobalActions } from './components/actions.js';
import views from './views/index.js';

route('/', 'today');
route('/welcome', 'welcome');
route('/journey', 'journey');
route('/chapter/:ch', 'chapter');
route('/lesson/:id', 'lesson');
route('/read/:ch', 'read');
route('/verse/:ch/:v', 'verse');
route('/explore', 'explore');
route('/concepts', 'concepts');
route('/concept/:id', 'concept');
route('/people', 'people');
route('/person/:id', 'person');
route('/search', 'search');
route('/saved', 'saved');
route('/settings', 'settings');
route('/about', 'about');
route('/signin', 'signin');
route('/guide', 'guide');

const main = document.getElementById('main');
let cleanup = null;
let token = 0;
let currentName = null;
let firstRender = true;

function errorView(e) {
  const stale = e instanceof StaleBuildError;
  const offline = e && e.message === 'offline';
  return {
    title: 'Something went wrong',
    html: html`<div class="wrap"><div class="empty" style="margin-top:15vh">
      <span class="empty__icon">${icon(offline ? 'cloud-off' : 'refresh', { size: 26 })}</span>
      <p class="empty__title">${stale ? 'Bhagavad Gita has been updated' : offline ? "You're offline" : 'That did not load'}</p>
      <p class="empty__text">${stale
        ? 'A newer version is available. Reload to continue where you left off.'
        : offline
          ? 'This part of the book has not been saved for offline reading yet. Reconnect and try again.'
          : 'Please try again. Your progress is safe.'}</p>
      <button type="button" class="btn btn--primary" data-reload>${stale ? 'Reload' : 'Try again'}</button>
    </div></div>`,
    mount(root) {
      root.querySelector('[data-reload]').addEventListener('click', () => location.reload());
    },
  };
}

async function show(r, { scrollY, direction }) {
  const my = ++token;
  if (r.name === 'today' && !hasAnyProgress() && !local().welcomed) {
    navigate('/welcome', { replace: true });
    return;
  }
  const view = views[r.name] || views.notfound;
  let result;
  try {
    result = await view(r.params, r);
  } catch (e) {
    console.error(e);
    result = errorView(e);
  }
  if (my !== token) return;
  if (result.redirect) {
    navigate(result.redirect, { replace: true });
    return;
  }
  const first = firstRender;
  const swap = () => {
    if (cleanup) {
      try {
        cleanup();
      } catch (e) {
        console.error(e);
      }
      cleanup = null;
    }
    document.getElementById('app').classList.toggle('app--focus', !!result.focus);
    document.documentElement.classList.toggle('is-focus', !!result.focus);
    // Each view gets its own container, discarded on the next navigation, so
    // listeners a view attaches to it can never accumulate across visits.
    main.innerHTML = `<div class="view" data-view="${r.name}">${result.html}</div>`;
    document.title = result.title ? `${result.title} · Bhagavad Gita` : 'Bhagavad Gita · Every verse, understood';
    setThemeColor(result.tint);
    currentName = r.name;
    renderNav(r.name);
    const c = result.mount ? result.mount(main.firstElementChild) : null;
    cleanup = typeof c === 'function' ? c : null;
    window.scrollTo(0, scrollY || 0);
    updateTopbar();
    if (r.hash) {
      const target = document.getElementById(r.hash.slice(1));
      if (target) target.scrollIntoView({ block: 'start' });
    }
    // Move focus into the new page for keyboard and screen reader users: to a
    // field the page marks with data-autofocus, or else to its heading. This
    // runs inside the swap, after the new page exists, even when the swap is
    // deferred by a view transition.
    const field = main.querySelector('[data-autofocus]');
    if (field) {
      field.focus({ preventScroll: true });
    } else if (!first) {
      const h = main.querySelector('h1');
      if (h) {
        h.setAttribute('tabindex', '-1');
        h.focus({ preventScroll: true });
      } else {
        main.focus({ preventScroll: true });
      }
    }
  };
  const animate = !firstRender && direction !== 'replace-silent' && document.startViewTransition && !reducedMotion();
  if (animate) {
    document.documentElement.dataset.nav = direction;
    const t = document.startViewTransition(swap);
    t.finished.finally(() => delete document.documentElement.dataset.nav);
  } else {
    swap();
  }
  firstRender = false;
}

function updateTopbar() {
  const bar = main.querySelector('.topbar');
  if (bar) bar.classList.toggle('is-scrolled', window.scrollY > 6);
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || location.hostname === 'localhost' && !window.__BG_SW__) return;
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.register('/sw.js').catch(() => {});
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) toast('Bhagavad Gita was updated.', { action: 'Refresh', onAction: () => location.reload(), timeout: 0 });
  });
  // Quietly cache every chapter for offline reading once things are idle.
  const prefetch = () => {
    const urls = Object.values(window.__BG__.data.chapters);
    navigator.serviceWorker.ready.then((reg) => reg.active && reg.active.postMessage({ type: 'prefetch', urls }));
  };
  if ('requestIdleCallback' in window) requestIdleCallback(prefetch, { timeout: 8000 });
  else setTimeout(prefetch, 5000);
}

async function boot() {
  applySettings();
  watchSystemTheme(applySettings);
  subscribe((doc, reason) => {
    if (['settings', 'sync', 'import', 'reset'].includes(reason)) applySettings();
  });
  onSync(() => {
    renderNav(currentName);
    for (const btn of main.querySelectorAll('.profile-btn')) {
      const fresh = document.createElement('div');
      fresh.innerHTML = String(profileButton({ className: btn.classList.contains('only-mobile') ? 'only-mobile' : '' }));
      btn.replaceWith(fresh.firstElementChild);
    }
  });
  window.addEventListener('scroll', updateTopbar, { passive: true });
  bindGlobalActions();
  try {
    await loadIndex();
  } catch (e) {
    main.innerHTML = String(errorView(e).html);
    main.querySelector('[data-reload]').addEventListener('click', () => location.reload());
    return;
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-back]');
    if (b) {
      e.preventDefault();
      back(b.dataset.back || '/');
    }
  });
  start(show);
  initSync();
  registerServiceWorker();
  if (getIndex().draft) console.warn('Draft build: some content is placeholder.');
}

boot();
